/**
 * Identidade de CADA geração pedida pela pessoa.
 *
 * Regras vinculativas:
 * - o `generationId` nunca deriva da cidade, dos locais nem do texto: dois
 *   pedidos idênticos, feitos em cliques diferentes, têm IDs diferentes;
 * - é monotónico dentro da sessão (o `seq` só sobe), para que uma resposta
 *   atrasada de um pedido anterior seja sempre reconhecível como antiga;
 * - a entropia é injetada (nunca `Math.random` em módulo), o que mantém o
 *   módulo puro e testável.
 */

export type GenerationIdentity = {
  routeInstanceId: string;
  generationId: string;
  seq: number;
  modeRevision: number;
};

export type GenerationIdFactory = {
  /** Novo pedido: nova instância de rota e novo `generationId`. */
  next(): GenerationIdentity;
  /** Última identidade emitida (`null` antes do primeiro pedido). */
  current(): GenerationIdentity | null;
};

export function createGenerationIdFactory(
  entropy: () => string = defaultEntropy,
): GenerationIdFactory {
  let seq = 0;
  let last: GenerationIdentity | null = null;
  return {
    next() {
      seq += 1;
      const token = entropy();
      last = {
        routeInstanceId: `route-${seq}-${token}`,
        generationId: `gen-${seq}-${token}`,
        seq,
        modeRevision: 0,
      };
      return last;
    },
    current() {
      return last;
    },
  };
}

function defaultEntropy(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (typeof g.crypto?.randomUUID === "function") return g.crypto.randomUUID().slice(0, 8);
  return Math.random().toString(36).slice(2, 10);
}

/** Ordem entre gerações: só a mais recente pode publicar. */
export function isOlderGeneration(current: { seq: number }, incoming: { seq: number }): boolean {
  return incoming.seq < current.seq;
}
